"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.busDimensionName = busDimensionName;
exports.busPositionalDimensionName = busPositionalDimensionName;
exports.parseBusMemberCount = parseBusMemberCount;
exports.getBusDetails = getBusDetails;
exports.getNetBusMemberDimensions = getNetBusMemberDimensions;
exports.getNetBusMemberDimensionsByName = getNetBusMemberDimensionsByName;
exports.inferNetBusName = inferNetBusName;
exports.findNetBusMemberDimensions = findNetBusMemberDimensions;
exports.buildBusPositionLookup = buildBusPositionLookup;
exports.busPortAlignsWithParent = busPortAlignsWithParent;
function busDimensionName(memberDim) {
    return `bus${memberDim.index}`;
}
function busPositionalDimensionName(positionalIndex) {
    return `bus${positionalIndex}-pos`;
}
function extractBusPrefix(busName) {
    const idx = busName.indexOf('[');
    return idx > 0 ? busName.substring(0, idx) : undefined;
}
function parseBusMemberCount(busName) {
    const match = busName.match(/\[(\d+)(?:\.\.|:)(\d+)\]/);
    if (!match) {
        return undefined;
    }
    return Math.abs(parseInt(match[2], 10) - parseInt(match[1], 10)) + 1;
}
function getBusDetails(busPortName) {
    const BusNameRegex = /^(.+)\[(\d+)(?:\.\.|:)(\d+)\]$/;
    const match = busPortName.match(BusNameRegex);
    if (!match) {
        return undefined;
    }
    const prefix = match[1];
    const start = parseInt(match[2], 10);
    const end = parseInt(match[3], 10);
    const count = Math.abs(end - start) + 1;
    const step = end >= start ? 1 : -1;
    return { match, count, start, step, prefix };
}
function getNetBusMemberDimensions(netBus) {
    return (netBus.NetScalar ?? [])
        .map((ns) => ns.Name)
        .filter(Boolean)
        .map((name, index) => ({ name, index }));
}
function getNetBusMemberDimensionsByName(netBus, busName) {
    const fallback = getNetBusMemberDimensions(netBus);
    if (busName) {
        const details = getBusDetails(busName);
        if (details) {
            const byName = tryIndexByPrefix(netBus, details.prefix, details.start, details.step);
            if (byName) {
                return byName;
            }
        }
    }
    const autoDetected = tryAutoDetectPrefix(netBus);
    if (autoDetected) {
        return autoDetected;
    }
    return fallback;
}
function tryIndexByPrefix(netBus, prefix, _start, _step) {
    const memberRegex = new RegExp(`^${escapeRegex(prefix)}(\\d+)$`);
    const result = [];
    for (const ns of netBus.NetScalar ?? []) {
        if (!ns.Name) {
            continue;
        }
        const match = ns.Name.match(memberRegex);
        if (!match) {
            return undefined;
        }
        const parsed = parseInt(match[1], 10);
        if (parsed < 0) {
            return undefined;
        }
        result.push({ name: ns.Name, index: parsed });
    }
    return result;
}
function tryAutoDetectPrefix(netBus) {
    const names = (netBus.NetScalar ?? []).map((ns) => ns.Name).filter((n) => Boolean(n));
    if (names.length === 0) {
        return undefined;
    }
    const splitRegex = /^(.+?)(\d+)$/;
    const parts = [];
    for (const name of names) {
        const m = name.match(splitRegex);
        if (!m) {
            return undefined;
        }
        parts.push({ name, prefix: m[1], digit: parseInt(m[2], 10) });
    }
    const commonPrefix = parts[0].prefix;
    if (!parts.every((p) => p.prefix === commonPrefix)) {
        return undefined;
    }
    return parts.map((p) => ({ name: p.name, index: p.digit }));
}
function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function inferNetBusName(netBus) {
    if (netBus.Name && getBusDetails(netBus.Name)) {
        return netBus.Name;
    }
    for (const wireBus of netBus.WireBus ?? []) {
        for (const alias of wireBus.Alias ?? []) {
            if (alias.Name && getBusDetails(alias.Name)) {
                return alias.Name;
            }
        }
    }
    return undefined;
}
function findNetBusMemberDimensions(page, busName) {
    for (const netBus of page.NetBus ?? []) {
        const aliasMatch = (netBus.WireBus ?? []).some((w) => (w.Alias ?? []).some((a) => a.Name === busName));
        if (aliasMatch || netBus.Name === busName) {
            return getNetBusMemberDimensions(netBus);
        }
    }
    const prefix = extractBusPrefix(busName);
    const expectedCount = parseBusMemberCount(busName);
    if (prefix) {
        for (const netBus of page.NetBus ?? []) {
            const members = (netBus.NetScalar ?? []).map((ns) => ns.Name).filter(Boolean);
            if (members.length > 0 &&
                (!expectedCount || members.length === expectedCount) &&
                members.every((name) => name.startsWith(prefix))) {
                return getNetBusMemberDimensions(netBus);
            }
        }
    }
    return undefined;
}
function buildBusPositionLookup(page) {
    const map = new Map();
    for (const netBus of page.NetBus ?? []) {
        const name = inferNetBusName(netBus);
        const members = getNetBusMemberDimensionsByName(netBus, name);
        if (members.length === 0) {
            continue;
        }
        for (const wireBus of netBus.WireBus ?? []) {
            map.set(`${wireBus.StartX},${wireBus.StartY}`, { members, name });
            map.set(`${wireBus.EndX},${wireBus.EndY}`, { members, name });
        }
    }
    return (positionKey) => map.get(positionKey);
}
function busPortAlignsWithParent(portMembers, parentMembers, parentName) {
    if (portMembers.length !== parentMembers.length) {
        return false;
    }
    const details = parentName ? getBusDetails(parentName) : undefined;
    if (!details) {
        const parentBits = new Set(parentMembers.map((m) => m.index));
        return portMembers.every((m) => parentBits.has(m.index));
    }
    return portMembers.every((m, i) => m.index === details.start + i * details.step);
}
//# sourceMappingURL=OrcadDsnBusUtils.js.map